import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, MapPin, Navigation } from 'lucide-react';
import { Geofence } from '@/types';
import { useLocation } from '@/contexts/LocationContext';

interface DraftZone {
  lat: number;
  lng: number;
}

export const GeofenceManager: React.FC = () => {
  const { geofences, addGeofence, deleteGeofence } = useLocation();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const circlesRef = useRef<Map<string, google.maps.Circle>>(new Map());
  const draftCircleRef = useRef<google.maps.Circle | null>(null);
  const clickListenerRef = useRef<any>(null);

  const [placementMode, setPlacementMode] = useState(false);
  const [draftZone, setDraftZone] = useState<DraftZone | null>(null);
  const [draftRadius, setDraftRadius] = useState(200);
  const [draftName, setDraftName] = useState('');
  const [notifyArrive, setNotifyArrive] = useState(true);
  const [notifyDepart, setNotifyDepart] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    if (!window.google?.maps) return;

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: 37.7749, lng: -122.4194 },
      zoom: 13,
      disableDefaultUI: true,
    });
  }, []);

  // Render existing geofence circles
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const existingIds = new Set(geofences.map((f) => f.id));

    // Remove circles for deleted geofences
    circlesRef.current.forEach((circle, id) => {
      if (!existingIds.has(id)) {
        circle.setMap(null);
        circlesRef.current.delete(id);
      }
    });

    // Add circles for new geofences
    geofences.forEach((fence) => {
      if (!circlesRef.current.has(fence.id)) {
        const circle = new window.google.maps.Circle({
          center: { lat: fence.lat, lng: fence.lng },
          radius: fence.radiusMeters,
          map: mapInstanceRef.current!,
          fillColor: '#6366f1',
          fillOpacity: 0.12,
          strokeColor: '#6366f1',
          strokeWeight: 2,
        });
        circlesRef.current.set(fence.id, circle);
      }
    });
  }, [geofences]);

  // Attach map click listener for placement mode
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    if (clickListenerRef.current) {
      (window.google as any).maps?.event?.removeListener(clickListenerRef.current);
    }
    if (placementMode) {
      clickListenerRef.current = mapInstanceRef.current.addListener(
        'click',
        (e: { latLng: { lat(): number; lng(): number } }) => {
          setDraftZone({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        }
      );
    }
  }, [placementMode]);

  // Update draft circle
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    draftCircleRef.current?.setMap(null);
    if (!draftZone) return;
    draftCircleRef.current = new window.google.maps.Circle({
      center: draftZone,
      radius: draftRadius,
      map: mapInstanceRef.current,
      fillColor: '#10b981',
      fillOpacity: 0.2,
      strokeColor: '#10b981',
      strokeWeight: 2,
      editable: true,
    });
    mapInstanceRef.current.setCenter(draftZone);
  }, [draftZone, draftRadius]);

  const useMyLocation = () => {
    navigator.geolocation.getCurrentPosition((pos) => {
      setDraftZone({ lat: pos.coords.latitude, lng: pos.coords.longitude });
    });
  };

  const handleSave = async () => {
    if (!draftZone || !draftName.trim()) return;
    setSaving(true);
    try {
      await addGeofence({
        name: draftName.trim(),
        lat: draftZone.lat,
        lng: draftZone.lng,
        radiusMeters: draftRadius,
        notifyOnArrive: notifyArrive,
        notifyOnDepart: notifyDepart,
      });
      setPlacementMode(false);
      setDraftZone(null);
      setDraftName('');
      setDraftRadius(200);
      draftCircleRef.current?.setMap(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteGeofence(id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 dark:text-slate-300">
          Geofence Zones
        </h3>
        <button
          onClick={() => { setPlacementMode(true); setDraftZone(null); }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white rounded-xl text-xs font-bold hover:bg-brand/90 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add Zone
        </button>
      </div>

      {/* Map */}
      <div className="relative rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 h-56">
        <div ref={mapRef} className="w-full h-full" />
        {!window.google?.maps && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
            <p className="text-sm text-slate-400">Map loading…</p>
          </div>
        )}
        {placementMode && (
          <div className="absolute top-2 left-2 right-2 bg-white/95 dark:bg-slate-900/95 rounded-xl p-2 text-xs text-slate-600 dark:text-slate-400 font-medium shadow">
            Click on the map to place a zone, or use your location below.
          </div>
        )}
      </div>

      {/* Placement form */}
      {placementMode && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">New Zone</span>
          </div>

          <button
            onClick={useMyLocation}
            className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400 hover:underline"
          >
            <Navigation className="w-3 h-3" /> Use my current location
          </button>

          <input
            type="text"
            placeholder="Zone name (e.g. School, Home)"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand/30"
          />

          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400">
              Radius: {draftRadius}m
            </label>
            <input
              type="range"
              min={100}
              max={5000}
              step={50}
              value={draftRadius}
              onChange={(e) => setDraftRadius(Number(e.target.value))}
              className="w-full mt-1 accent-emerald-500"
            />
          </div>

          <div className="flex gap-4">
            <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 cursor-pointer">
              <input
                type="checkbox"
                checked={notifyArrive}
                onChange={(e) => setNotifyArrive(e.target.checked)}
                className="rounded accent-emerald-500"
              />
              Notify on arrive
            </label>
            <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 cursor-pointer">
              <input
                type="checkbox"
                checked={notifyDepart}
                onChange={(e) => setNotifyDepart(e.target.checked)}
                className="rounded accent-emerald-500"
              />
              Notify on depart
            </label>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={!draftZone || !draftName.trim() || saving}
              className="flex-1 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving…' : 'Save Zone'}
            </button>
            <button
              onClick={() => { setPlacementMode(false); setDraftZone(null); draftCircleRef.current?.setMap(null); }}
              className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-xl text-xs font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Existing zones list */}
      {geofences.length === 0 && !placementMode && (
        <p className="text-sm text-slate-400 dark:text-slate-500 italic text-center py-2">
          No zones configured yet. Add one to get started.
        </p>
      )}
      <div className="space-y-2">
        {geofences.map((fence) => (
          <div
            key={fence.id}
            className="flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl"
          >
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-brand" />
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{fence.name}</p>
                <p className="text-xs text-slate-500">{fence.radiusMeters}m radius</p>
              </div>
            </div>
            <button
              onClick={() => handleDelete(fence.id)}
              disabled={deletingId === fence.id}
              className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors disabled:opacity-50"
              title="Delete zone"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
